/**
 * Punto di ingresso dell'ordine manuale dalla pagina del portafoglio (TODO.md).
 *
 * Separato dal modal per la stessa ragione di `PortfolioSyncButton`: qui vive la
 * decisione **se** offrire il comando, là **come** funziona. Tenerle insieme
 * costringerebbe la pagina a montare il modal per scoprire che non c'è niente da
 * mostrare.
 *
 * ## Quando non compare
 *
 * Senza una connessione broker attiva **con trading abilitato** non c'è nessun
 * ordine da inviare. Il pulsante sparisce del tutto invece di restare spento:
 * `trading_enabled` è un interruttore della connessione, e si accende dalla
 * pagina Connessioni — un pulsante disabilitato qui inviterebbe a cercare la
 * causa nel posto sbagliato.
 *
 * ## Quali titoli
 *
 * Le posizioni del portafoglio più i candidati in wishlist per quel portafoglio:
 * sono i due insiemi su cui un ordine ha senso — quello che si possiede e si può
 * vendere, e quello che si è già deciso di voler comprare. Un elenco di tutti
 * gli strumenti del catalogo trasformerebbe la scelta del titolo nel passaggio
 * più rischioso dell'operazione.
 */

import { useMemo, useState } from 'react';
import { useConnections } from '@/features/connectors/api';
import { ManualOrderModal } from '@/features/connectors/ManualOrderModal';
import { useWishlist } from '@/features/wishlist/api';
import { Button } from '@/ui';
import type { Holding } from './api';

type Props = {
  portfolioId: string;
  holdings: Holding[];
  /**
   * Aperto **dentro** una posizione (F-136): il titolo e' gia' deciso.
   *
   * ⚠️ Il titolo entra nell'elenco anche quando NON e' fra le `holdings` — una
   * posizione chiusa o non ancora fotografata resterebbe altrimenti senza
   * pulsante, ed e' proprio da li' che si vuole poter ricomprare.
   */
  fixedInstrument?: { id: string; name: string; symbol: string | null; trading212Ticker?: string | null };
  defaultSide?: 'buy' | 'sell';
  label?: string;
};

export function ManualOrderButton({ portfolioId, holdings, fixedInstrument, defaultSide, label }: Props) {
  const connections = useConnections();
  const wishlist = useWishlist(portfolioId);
  const [open, setOpen] = useState(false);

  // Solo i BROKER: un exchange crypto ha il proprio comando (`CryptoTradeButton`),
  // con coppie e saldi al posto di ticker e posizioni.
  const connection = (connections.data?.items ?? []).find(
    (item) =>
      item.portfolio.id === portfolioId && item.kind !== 'crypto_exchange' && item.status === 'active' && item.canPlaceOrders,
  );

  const instruments = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; symbol: string | null; trading212Ticker?: string | null }>();
    for (const holding of holdings) {
      byId.set(holding.instrument.id, {
        id: holding.instrument.id,
        name: holding.instrument.name,
        symbol: holding.instrument.symbol,
        // ⚠️ Anche le posizioni portano il ticker (F-139). Prima lo portava
        // solo la wishlist: una posizione fuori wishlist — o con la riga
        // scaduta, che e' il caso normale dopo sette giorni — compariva nel
        // modal come «ticker non associato» mentre il broker l'aveva appena
        // sincronizzata. L'ordine partiva lo stesso: falso era l'avviso.
        trading212Ticker: holding.instrument.trading212Ticker,
      });
    }
    // La wishlist DOPO le posizioni, e sovrascrive: un titolo in entrambe ha
    // lo stesso ticker, e la wishlist e' la riga che l'utente ha toccato piu'
    // di recente.
    for (const item of wishlist.data?.items ?? []) {
      byId.set(item.instrument.id, {
        id: item.instrument.id,
        name: item.instrument.name,
        symbol: item.instrument.symbol,
        trading212Ticker: item.instrument.trading212Ticker,
      });
    }
    // Il titolo fissato entra comunque: senza, una posizione chiusa o non
    // ancora sincronizzata non avrebbe alcun pulsante.
    if (fixedInstrument && !byId.has(fixedInstrument.id)) {
      byId.set(fixedInstrument.id, { ...fixedInstrument });
    }
    return [...byId.values()].sort((a, b) => (a.symbol ?? a.name).localeCompare(b.symbol ?? b.name));
  }, [holdings, wishlist.data, fixedInstrument]);

  if (!connection || instruments.length === 0) return null;

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {label ?? 'Ordine manuale'}
      </Button>
      {open && (
        <ManualOrderModal
          connectionId={connection.id}
          instruments={instruments}
          {...(fixedInstrument ? { lockedInstrumentId: fixedInstrument.id } : {})}
          {...(defaultSide ? { defaultSide } : {})}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
